import * as Sentry from '@sentry/react';
import { QueryCache, QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';

import { CookieStorage } from '@crossnokaye/ux-common';

import { onQueryError, onQueryWarning } from '@/queries/query-utils';
import { QueryToast } from '@/queries/types';
import { routeTree } from '@/routeTree.gen';
import { getApiErrorMessage } from '@/utils/error-utils';

export const cookieStorage = new CookieStorage({});

// Create queryClient with configuration
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (Sentry.isInitialized()) {
        Sentry.captureException(error, {
          extra: { isQueryError: true, query },
        });
      }

      const queryMeta = query.meta?.toast as QueryToast;
      const errorMessage = getApiErrorMessage(error);

      const queryToast = {
        severity: queryMeta?.severity || 'error',
        message: queryMeta?.message || errorMessage,
        timeout: queryMeta?.timeout || 15 * 1000,
      };

      if (queryToast.severity === 'error') {
        onQueryError(queryToast.message, queryToast.timeout);
      } else if (queryToast.severity === 'warning') {
        onQueryWarning(queryToast.message, queryToast.timeout);
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

// create router instance with auth context placeholder
export const router = createRouter({
  routeTree,
  context: {
    queryClient,
    auth: undefined, // This will be provided by RouterProvider
    ldClient: undefined, // This will be provided by RouterProvider
    atlasClient: undefined, // This will be provided by RouterProvider
    gaiaClient: undefined, // This will be provided by RouterProvider
    updateFacilityCache: undefined, // This will be provided by RouterProvider
  },
});

// register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
